import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Text, Tooltip } from 'rizzui';
import { ActivityChangesModalContent, formatActivityDate, renderActivityChangesSummary, renderActivityIpAddress } from '../../../../../components/activity/ActivityAuditDetail';
import AsyncImg from '../../../../../components/extras/AsyncImg';
import ModalForm from '../../../../../components/forms/ModalForm';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import AvatarCard from '../../../../../components/ui/AvatarCard';
import useFetch from '../../../../../hooks/useFetch';
import Page from '../../../../../layout/Page/Page';
import { menuRoutes } from '../../../../../router/menu';
import { ContractService } from '../../../../../services/contract/contractService';
import { ContractActivity, ContractActivityApiResponse, ContractActivityChange } from '../../../../../type/entities/contract-type';
import ContractActivityHistoryFilters from './ContractActivityHistoryFilters';

interface ContractActivityHistoryProps { }

const columnOrder = [
    'id',
    'user',
    'company',
    'actionType',
    'changes',
    'ipAddress',
    'createdAt',
    'comment',
];

const actionTypeLabels: Record<string, string> = {
    contract_created: 'Contrato creado',
    contract_draft_created: 'Borrador creado',
    contract_updated: 'Contrato editado',
    contract_draft_updated: 'Borrador editado',
    contract_completed_from_draft: 'Borrador completado',
    contract_status_changed: 'Estado contrato',
    contract_product_status_changed: 'Estado producto',
    contract_invoicing_status_changed: 'Estado facturación',
    contract_invoicing_status_synced: 'Facturación sincronizada',
    contract_incidence_status_changed: 'Estado incidencia',
    contract_blocked_status_changed: 'Estado bloqueo',
    contract_commission_status_changed: 'Estado comisión',
    contract_score_updated: 'Puntuación',
    contract_comment_added: 'Comentario añadido',
    contract_comment_updated: 'Comentario editado',
    contract_incidence_registered: 'Incidencia registrada',
    contract_signature_link_sent: 'Enlace de firma',
    contract_documents_signed: 'Documentos firmados',
    contract_signed_documents_emailed: 'Documentos enviados',
    contract_signed_documents_email_failed: 'Error envío email',
    contract_signed_documents_email_skipped: 'Envío omitido',
    contract_points_transferred: 'Transferencia de puntos',
    contract_fundae_grouping_added: 'Agrupación Fundae',
    contract_tutor_invoice_linked: 'Factura tutor',
    contract_sepa_return_reconciled: 'Devolución SEPA',
};

const ContractActivityHistory: React.FC<ContractActivityHistoryProps> = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [selectedChanges, setSelectedChanges] = useState<ContractActivityChange[] | null>(null);
    const { userCan } = usePrivilege();

    const openChangesModal = (changes: ContractActivityChange[]) => {
        setSelectedChanges(changes);
    };

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new ContractService()).listActivityHistory({ ...filters, filter_filters: { ...filters.filter_filters, contract: id } });
        return response.getResponseData() as ContractActivityApiResponse;
    }, [id, filters]));

    return (
        <Page container='fluid' className='mt-5'>
            {data && (
                <CustomTable
                    id={'changelogs-table'}
                    columnOrder={columnOrder}
                    data={data?.data}
                    isLoading={loading}
                    columnsNotShown={['id', 'client', 'contract', 'updatedAt']}
                    overrideColumns={[
                        {
                            key: 'user',
                            render: (row: any) => {
                                const canView = userCan('list_user', 'user');
                                const entity = (row.row.original as ContractActivity)?.user;

                                if (entity && !Object.prototype.hasOwnProperty.call(entity, 'profileImg')) {
                                    return (
                                        <AvatarCard
                                            textClassName='font-bold text-primary'
                                            src={'default-avatar.png'}
                                            name={entity.name + (entity.lastName ? ` ${entity.lastName}` : '')}
                                            link={canView ? `${menuRoutes.users.path}/${entity.id}/profile${menuRoutes.users.profile.info}` : undefined}
                                        />
                                    )
                                } else if (entity) {
                                    const content = (
                                        <div className="flex items-center gap-3">
                                            <AsyncImg id={entity.id} className="mx-auto d-block rounded w-[40px] h-[40px] object-cover" />
                                            <Text>{entity.name + (entity.lastName ? ` ${entity.lastName}` : '')}</Text>
                                        </div>
                                    );

                                    if (canView) {
                                        return (
                                            <Link to={`/users/${entity.id}/edit`}>
                                                {content}
                                            </Link>
                                        )
                                    }

                                    return content;
                                } else {
                                    return <Text>N/A</Text>
                                }
                            },
                        },
                        {
                            key: 'company',
                            render: (row: any) => {
                                const entity = (row.row.original as ContractActivity).company;
                                return (
                                    <Link to={`/coorporation/${entity.id}/view`}>
                                        <div className="flex items-center gap-3">
                                            <Text>{entity.name}</Text>
                                        </div>
                                    </Link>
                                )
                            },
                        },
                        {
                            key: 'actionType',
                            render: (row: any) => {
                                const actionType = (row.row.original as ContractActivity).actionType || 'legacy';
                                return <Text>{actionTypeLabels[actionType] || actionType}</Text>;
                            },
                        },
                        {
                            key: 'changes',
                            render: (row: any) => renderActivityChangesSummary(
                                (row.row.original as ContractActivity).changes,
                                openChangesModal
                            ),
                        },
                        {
                            key: 'ipAddress',
                            render: (row: any) => renderActivityIpAddress(row.row.original as ContractActivity),
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => <Text>{formatActivityDate((row.row.original as ContractActivity).createdAt as any)}</Text>,
                        },
                        {
                            key: 'comment',
                            render: (row: any) => {
                                const comment = (row.row.original as ContractActivity).comment;
                                return (
                                    <Tooltip
                                        size="md"
                                        content={<div className='tooltip-container'>{comment}</div>}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>{comment.substring(0, 100)} {comment.length > 100 && '...'}</div>
                                    </Tooltip>
                                )
                            },
                        }
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}
            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Actividad'}>
                <ContractActivityHistoryFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
            <ModalForm
                isOpen={selectedChanges !== null}
                onClose={() => setSelectedChanges(null)}
                title='Detalle de cambios'
                size='xl'
            >
                <ActivityChangesModalContent changes={selectedChanges} />
            </ModalForm>
        </Page>
    );
};

export default ContractActivityHistory;
